import { Api, Model, ModelsApi } from '@ps-int/transport';

import { isSelectPromoModelResponse } from '../../../../../common/helpers/promozavr-validation';
import { PROMOZAVR_MODEL } from './types';
import type { SelectPromoModelInput, SelectPromoModelResponse } from './types';

type PromozavrModels = {
    [PROMOZAVR_MODEL]: Model<SelectPromoModelInput, SelectPromoModelResponse>;
};

type PromozavrModelsApiLike = Pick<ModelsApi<PromozavrModels>, 'request'>;

let defaultModelsApi: PromozavrModelsApiLike | undefined;

function toPromozavrErrorMessage(error: unknown) {
    if (typeof error === 'string') {
        return error;
    }

    if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
        return error.message;
    }

    return 'Failed to select promo';
}

function parseSelectPromoTransportResult(result: unknown): SelectPromoModelResponse {
    if (!result || typeof result !== 'object') {
        throw new Error('Failed to select promo');
    }

    if ('error' in result) {
        throw new Error(toPromozavrErrorMessage(result.error));
    }

    if (!('data' in result) || !isSelectPromoModelResponse(result.data)) {
        throw new Error('Failed to select promo');
    }

    return result.data;
}

export function createPromozavrModelsApi(): PromozavrModelsApiLike {
    const api = new Api('/api/models');

    return new ModelsApi<PromozavrModels>(api, {
        modelsQueryParameter: '_models',
    });
}

function getDefaultPromozavrModelsApi(): PromozavrModelsApiLike {
    if (!defaultModelsApi) {
        defaultModelsApi = createPromozavrModelsApi();
    }

    return defaultModelsApi;
}

export function createPromozavrApi(modelsApi?: PromozavrModelsApiLike) {
    return {
        async selectPromo(requestParams: SelectPromoModelInput): Promise<SelectPromoModelResponse> {
            const [result] = await (modelsApi ?? getDefaultPromozavrModelsApi()).request([
                {
                    name: PROMOZAVR_MODEL,
                    params: requestParams,
                },
            ]);

            return parseSelectPromoTransportResult(result);
        },
    };
}

const defaultPromozavrApi = createPromozavrApi();

export const { selectPromo } = defaultPromozavrApi;
